import type { Species } from '../../types';
import { getSpecies, packPool } from '../region';
import { studyCodes } from '../ignored';
import { studyRemainder } from '../fsrs';
import { narrow } from './focus';
import { buildChoices, buildPairChoices } from './distractors';
import { DEFAULT_ROUND_LEN, newBirdShare } from './settings';
import { fakeBinomial, isFakeName, pickFakeNames } from './fake-names';

// Name Match: recall the other half of a species' name pair. Both directions
// share one `code:names` FSRS card — the skill being trained is "knows this
// bird's name pairing", not two independent decks.

/** Which way a single question runs. */
export type NameDirection = 'toSci' | 'toCommon';

/** The user's session setting; `mixed` resolves per question. */
export type DirectionMode = NameDirection | 'mixed';

/** Multiple choice, or flip-the-card and self-grade. */
export type NameMode = 'choice' | 'flip';

export interface NameQuestion {
  /** The species being tested. */
  target: Species;
  /** Resolved direction for this question. */
  direction: NameDirection;
  /**
   * Answer choices, target included, in display order. Species codes, except
   * that a common → scientific question may carry a made-up binomial or two —
   * see `isFakeName` and core/quiz/fake-names.ts.
   */
  choices: string[];
}

const CHOICES = 4; // 1 correct + 3 distractors

/**
 * How often a common → scientific question swaps a real decoy for a made-up
 * binomial, and how often, when it does, it swaps two.
 *
 * Often enough that "one of these isn't a real bird" is never a surprise, and
 * rare enough that the question stays mostly about telling real birds apart.
 * Only that direction: the other one is answered with common names, and a fake
 * "Pacific Spotted Sparrow" would teach nothing about the Latin.
 */
const FAKE_CHANCE = 0.3;
const SECOND_FAKE_CHANCE = 0.25;

/** The side of the pair shown to the user. */
export function promptText(sp: Species, d: NameDirection): string {
  return d === 'toSci' ? sp.common : sp.sci;
}

/** The side of the pair the user has to produce. */
export function answerText(sp: Species, d: NameDirection): string {
  return d === 'toSci' ? sp.sci : sp.common;
}

/**
 * What a choice button says: the other half of that bird's name, or the fake
 * binomial itself when the choice is one.
 */
export function choiceText(choice: string, d: NameDirection): string {
  if (isFakeName(choice)) return fakeBinomial(choice);
  const sp = getSpecies(choice);
  return sp ? answerText(sp, d) : choice;
}

/**
 * Swap `n` of a question's real decoys for fake binomials, in place.
 *
 * The target is never touched, and neither is `keep` — the pair drill's other
 * bird, which is the whole point of that question. The fakes take the slots of
 * the decoys they replace, so the order the choices were shuffled into stands.
 */
function withFakes(
  target: Species,
  choices: string[],
  n: number,
  rnd: () => number,
  keep?: string,
): string[] {
  const slots = choices
    .map((c, i) => (c === target.code || c === keep ? -1 : i))
    .filter((i) => i >= 0);
  const onCard = new Set(
    choices.map((c) => getSpecies(c)?.sci).filter((s): s is string => !!s),
  );
  const fakes = pickFakeNames(target, Math.min(n, slots.length), rnd, onCard);
  const out = choices.slice();
  for (const fake of fakes) {
    const at = slots.splice(Math.floor(rnd() * slots.length), 1)[0];
    out[at] = fake;
  }
  return out;
}

/** Resolve the session setting into a concrete direction for one question. */
export function resolveDirection(
  mode: DirectionMode,
  rnd: () => number = Math.random,
): NameDirection {
  if (mode === 'mixed') return rnd() < 0.5 ? 'toSci' : 'toCommon';
  return mode;
}

/**
 * Build a single Name Match question for a known species code.
 *
 * `partner` is the pair drill's other bird, guaranteed onto the answer list;
 * see buildPairChoices.
 */
export function buildNameQuestion(
  code: string,
  mode: DirectionMode,
  rnd: () => number = Math.random,
  partner?: string,
): NameQuestion | null {
  const target = getSpecies(code);
  if (!target) return null;
  const direction = resolveDirection(mode, rnd);
  // Choices are built even in flip mode: it costs nothing and keeps one
  // question shape for both UIs.
  const pool = packPool(code, studyCodes());
  let choices = partner
    ? buildPairChoices(code, partner, pool, CHOICES - 1, 'names', rnd)
    : buildChoices(code, pool, CHOICES - 1, 'names', rnd);
  // Drawn after the real choices, so those land exactly where they did before
  // fake names existed.
  if (direction === 'toSci' && rnd() < FAKE_CHANCE) {
    const n = rnd() < SECOND_FAKE_CHANCE ? 2 : 1;
    choices = withFakes(target, choices, n, rnd, partner);
  }
  return { target, direction, choices };
}

/**
 * Build a Name Match session from the FSRS study queue. Unlike Sound ID no
 * species is ever skipped — every species has both names.
 *
 * `len` is the round length the setup screen chose; see quiz/settings.ts.
 */
export async function buildNamesSession(
  mode: DirectionMode,
  now: Date = new Date(),
  rnd: () => number = Math.random,
  done?: string,
  len: number = DEFAULT_ROUND_LEN,
  /**
   * The birds this round is confined to, when it was started from a filtered
   * list rather than from the study grid — see core/quiz/focus.ts. Undefined is
   * an ordinary round over everything this mode can ask about.
   */
  focus?: readonly string[],
): Promise<NameQuestion[]> {
  const queue = await studyRemainder(
    'names',
    narrow(studyCodes(), focus),
    now,
    len,
    done,
    await newBirdShare('names'),
  );
  const questions: NameQuestion[] = [];
  for (const code of queue) {
    const q = buildNameQuestion(code, mode, rnd);
    if (q) questions.push(q);
  }
  return questions;
}
